if (!customElements.get("fabrics-form")) {
  class FabricsForm extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.optionsLoaded = false;
      this.optionsLoading = false;
      this.appObserver = null;
      this.submitObserver = null;

      this.refreshSelectors();
      this.observeAppEmbed();
      this.listenForLazyOptions();
    }

    refreshSelectors() {
      this.selectors = {
        drawer: this.closest("drawer-section"),
        form: this.querySelector(".form--validate"),
        options: this.querySelectorAll('input[type="checkbox"][name="fabrics"]'),
        next: this.querySelector("[data-next]"),
        submit: this.querySelector('button[type="submit"]'),
        limit_msg: this.querySelector("[data-limit-message]"),
        app: this.querySelector(".fabrics__app"),
        app_form: this.selectors ? this.selectors.app_form : void 0,
        lazy_options: this.querySelector("[data-fabrics-options]"),
      };
    }

    observeAppEmbed() {
      if (!this.selectors.app || this.appObserver) return;

      this.resolveAppForm();

      this.appObserver = new MutationObserver(() => {
        this.resolveAppForm();
      });

      this.appObserver.observe(this.selectors.app, {
        childList: true,
        subtree: true,
      });
    }

    resolveAppForm() {
      const appEmbed = this.selectors.app.querySelector("#app-embed");
      if (!appEmbed || !appEmbed.shadowRoot) return;

      this.selectors.app_form = appEmbed.shadowRoot;
      this.bind();
    }

    listenForLazyOptions() {
      if (!this.selectors.drawer || !this.selectors.lazy_options) return;

      window.addEventListener("drawerOpen", (event) => {
        if (event.detail.id === this.selectors.drawer.id) {
          this.loadOptions();
        }
      });
    }

    loadOptions() {
      const target = this.selectors.lazy_options;
      const url = target && target.dataset.lazyUrl;

      if (!target || !url || this.optionsLoaded || this.optionsLoading) {
        this.bind();
        return;
      }

      this.optionsLoading = true;
      this.classList.add("loading");

      fetch(url, { method: "GET" })
        .then((response) => {
          if (!response.ok) throw new Error("HTTP " + response.status);
          return response.text();
        })
        .then((html) => {
          target.innerHTML = html;
          this.injectShippingLabels(target);
          this.optionsLoaded = true;
          this.optionsLoading = false;
          this.classList.remove("loading");
          this.refreshSelectors();
          this.observeAppEmbed();
          this.bind();

          if (this.selectors.drawer) {
            this.selectors.drawer.dispatchEvent(new CustomEvent("drawerResize"));
          }
        })
        .catch((error) => {
          this.optionsLoading = false;
          this.classList.remove("loading");
          console.error("Error loading fabric options:", error);
        });
    }

    injectShippingLabels(container) {
      const shipping = container.dataset.shipping;
      if (!shipping) return;

      container.querySelectorAll(".fabrics__group-header > div").forEach((header) => {
        if (header.querySelector("[data-fabrics-shipping]")) return;

        const label = document.createElement("p");
        label.dataset.fabricsShipping = "true";
        label.textContent = shipping;
        header.appendChild(label);
      });
    }

    bind() {
      if (this.initialized) return;

      this.refreshSelectors();

      if (
        !this.selectors.form ||
        !this.selectors.next ||
        !this.selectors.submit ||
        !this.selectors.limit_msg ||
        !this.selectors.app_form ||
        this.selectors.options.length < 1
      ) {
        return;
      }

      this.selectors.options.forEach((option) => {
        option.addEventListener("change", () => {
          this.selected_options = [...this.selectors.options]
            .filter((item) => item.checked)
            .map((item) => item.value);

          if (this.selected_options.length > 5) {
            option.checked = false;
            this.selectors.limit_msg.style.display = "flex";
            this.selectors.next.disabled = false;
            return;
          }

          if (this.selected_options.length > 0) {
            this.selectors.next.disabled = false;
            this.selectors.limit_msg.style.display = "none";
          } else {
            this.selectors.next.disabled = true;
          }

          const fabricsInput = this.selectors.app_form.querySelector(
            '[id="custom#wybrane_tkaniny"]',
          );

          if (fabricsInput) {
            fabricsInput.value = this.selected_options.join(", ");
            fabricsInput.dispatchEvent(new Event("input"));
          }
        });
      });

      this.selectors.form.querySelectorAll("[data-sync]").forEach((input) => {
        input.addEventListener("change", () => {
          input.classList.add("touched");

          const target = this.selectors.app_form.querySelector(
            `[id="${input.getAttribute("data-sync")}"]`,
          );

          if (!target) return;

          if (input.type === "checkbox") {
            if (target.checked !== input.checked) {
              target.click();
            }
          } else {
            target.value = input.value;
            target.dispatchEvent(new Event("input"));
          }
        });
      });

      this.validate();

      this.selectors.next.addEventListener("click", () => {
        this.classList.remove("fabrics--1");
        this.classList.add("fabrics--2");

        if (this.selectors.drawer) {
          this.selectors.drawer.dispatchEvent(new CustomEvent("drawerResize"));
        }
      });

      this.selectors.submit.addEventListener("click", (event) => {
        event.preventDefault();

        if (!this.validator.validate()) return;

        const submit = this.selectors.app_form.querySelector('button[type="submit"]');
        if (!submit) return;

        this.classList.add("loading");
        submit.click();
      });

      this.submitObserver = new MutationObserver((entries) => {
        for (const entry of entries) {
          if (
            entry.type === "attributes" &&
            entry.attributeName === "data-current-step"
          ) {
            this.classList.remove("fabrics--1");
            this.classList.remove("fabrics--2");
            this.classList.add("fabrics--3");
            this.classList.remove("loading");
          }
        }
      });

      this.submitObserver.observe(this.selectors.app_form, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      this.initialized = true;
    }

    validate() {
      this.validator = new Pristine(this.selectors.form, {
        classTo: "form__field",
        errorClass: "form__row--error",
        errorTextParent: "form__field",
        errorTextClass: "form__error",
      });

      const phone = this.selectors.form.querySelector('input[type="tel"]');

      if (phone) {
        this.validator.addValidator(
          phone,
          function (value) {
            return (
              !(value.length > 0) ||
              /^[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\./0-9]*$/.test(value)
            );
          },
          phone.getAttribute("data-pristine-phone-message"),
          5,
          false,
        );
      }

      this.querySelectorAll("select").forEach((select) => {
        const placeholder = select.querySelector("option[disabled]");
        if (!placeholder) return;

        this.validator.addValidator(
          select,
          function (value) {
            return value !== placeholder.innerText;
          },
          select.getAttribute("data-pristine-required-message"),
          5,
          false,
        );
      });
    }
  }

  customElements.define("fabrics-form", FabricsForm);
}
